import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { Photos, Sync as SyncAPI, Billing, BillingEmail } from "../lib/api";
import { LocalStore } from "../lib/storage";
import { useAuth } from "../context/AuthContext";
import { dataUrlToBlob } from "../lib/exif";
import {
  CloudArrowUp,
  DownloadSimple,
  FloppyDisk,
  Key,
  Link as LinkIcon,
  CheckCircle,
  WarningCircle,
  ArrowsClockwise,
  Copy,
  Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SyncPage() {
  const nav = useNavigate();
  const [active, setActive] = useState(LocalStore.getActiveSession());
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // {sent, total, current, ok, failed}
  const [receipts, setReceipts] = useState([]);
  const [contract, setContract] = useState(null);
  const [validation, setValidation] = useState(null); // {reachable, latency_ms, status, probe, last_checked} | null
  const [validating, setValidating] = useState(false);
  const [sparkline, setSparkline] = useState(null);   // {items, count, success_rate, base_url, window_hours}
  // iter-25: end-to-end handshake verifier (dry-run upload to parent)
  const [handshaking, setHandshaking] = useState(false);
  const [handshakeResult, setHandshakeResult] = useState(null);

  // ============ Billing (iter-17) ============
  const [billingEmail, setBillingEmail] = useState(BillingEmail.get());
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [emailInput, setEmailInput] = useState(BillingEmail.get());
  const [upgrading, setUpgrading] = useState(false);
  // iter-24: Customer Portal
  const [portalLoading, setPortalLoading] = useState(false);

  const refreshBilling = async (email) => {
    const e = (email || "").trim().toLowerCase();
    if (!e) { setSyncEnabled(false); return; }
    try {
      const r = await Billing.status(e);
      setSyncEnabled(!!r.sync_enabled);
    } catch {
      setSyncEnabled(false);
    }
  };

  // iter-42: signed-in Google email is the billing identity — prefill + persist.
  const { user } = useAuth();
  useEffect(() => {
    const e = (user?.email || "").trim().toLowerCase();
    if (e && e !== billingEmail) { BillingEmail.set(e); setBillingEmail(e); setEmailInput(e); }
  }, [user, billingEmail]);

  // On boot: if we have an email cached, refresh billing status.
  useEffect(() => {
    if (billingEmail) refreshBilling(billingEmail);
  }, [billingEmail]);

  // Post-checkout poll. When Stripe redirects back to /sessions?upgraded=1&session_id=...,
  // the Sessions page handles the poll; here on /sync?canceled=1 we just toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled") === "1") {
      toast("CHECKOUT CANCELED");
      // Clean the URL without reloading
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Inject Stripe Buy Button script once (parallel checkout path that bypasses
  // STRIPE_PRICE_ID_STARTER env var — the price is baked into the button id).
  // Buyer's email is captured by Stripe; our webhook re-parses raw event body
  // to extract customer_details.email and flip subscription_status.
  useEffect(() => {
    const SCRIPT_ID = "stripe-buy-button-js";
    if (document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = "https://js.stripe.com/v3/buy-button.js";
    document.head.appendChild(s);
  }, []);

  const handleUpgrade = async () => {
    const e = (emailInput || "").trim().toLowerCase();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("ENTER A VALID EMAIL");
      return;
    }
    BillingEmail.set(e);
    setBillingEmail(e);
    setUpgrading(true);
    try {
      const r = await Billing.createCheckoutSession({ email: e });
      window.location.href = r.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "CHECKOUT FAILED";
      toast.error(String(msg).toUpperCase().slice(0, 120));
      setUpgrading(false);
    }
  };

  // iter-24: open Stripe-hosted Customer Portal for billingEmail
  const openPortal = async () => {
    if (!billingEmail) {
      toast.error("NO BILLING EMAIL ON FILE");
      return;
    }
    setPortalLoading(true);
    try {
      const r = await Billing.portalSession(billingEmail);
      if (r?.url) {
        window.location.href = r.url;
        return;
      }
      throw new Error("No portal URL returned");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "PORTAL OPEN FAILED";
      toast.error(String(detail).toUpperCase().slice(0, 140));
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const local = LocalStore.getSyncConfig() || {};
      setBaseUrl(local.base_url || local.api_url || "");
      setApiKey(local.api_key || "");
      try {
        const remote = await SyncAPI.getConfig();
        if (remote) {
          setBaseUrl(remote.base_url || local.base_url || "");
          // Server returns a MASKED key (••••last4); prefer the real local copy.
          setApiKey(local.api_key || remote.api_key || "");
          setContract({ ingest_path_template: remote.ingest_path_template });
          if (remote.validation) setValidation(remote.validation);
        }
      } catch (err) {
        // Offline: fall back to local config silently.
        console.debug("sync: getConfig failed (offline ok)", err);
      }
      try {
        const c = await SyncAPI.getIngestContract();
        setContract((prev) => ({ ...(prev || {}), ...c }));
      } catch (err) {
        console.debug("sync: getIngestContract failed (offline ok)", err);
      }
      if (active) {
        try {
          const list = await Photos.list(active.id);
          setPhotos(list);
        } catch (err) {
          console.error("sync: Photos.list failed", err);
        }
        try {
          const rec = await SyncAPI.listReceipts(active.id);
          // v1 returns CursorPage { items, next_cursor, has_more }
          setReceipts(Array.isArray(rec) ? rec : rec.items || []);
        } catch (err) {
          console.error("sync: listReceipts failed", err);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sparkline fetch — debounced on baseUrl change. Read-only; never triggers a probe.
  useEffect(() => {
    if (!baseUrl.trim()) {
      setSparkline(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const s = await SyncAPI.sparkline(baseUrl.trim(), 24);
        setSparkline(s);
      } catch {
        setSparkline(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [baseUrl]);

  const refreshPhotos = async () => {
    if (!active) return;
    try {
      const list = await Photos.list(active.id);
      setPhotos(list);
    } catch (err) {
      console.error("sync: refreshPhotos failed", err);
    }
    refreshReceipts();
  };

  const refreshReceipts = async () => {
    if (!active) return;
    try {
      const rec = await SyncAPI.listReceipts(active.id);
      setReceipts(Array.isArray(rec) ? rec : rec.items || []);
    } catch (err) {
      console.error("sync: refreshReceipts failed", err);
    }
  };

  const copyReceipt = async (r) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(r, null, 2));
      toast.success("Receipt copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const clearAllReceipts = async () => {
    if (!active) return;
    if (!window.confirm("Clear all receipts for this session?")) return;
    try {
      await SyncAPI.clearReceipts(active.id);
      setReceipts([]);
      toast.success("Receipts cleared");
    } catch {
      toast.error("Failed to clear");
    }
  };

  const unsyncedCount = photos.filter((p) => !p.synced_at).length;
  const syncedCount = photos.length - unsyncedCount;

  const saveConfig = async () => {
    if (!baseUrl.trim() || !apiKey.trim()) {
      toast.error("Both Base URL and API key are required");
      return;
    }
    setSavingCfg(true);
    try {
      LocalStore.setSyncConfig({ base_url: baseUrl.trim(), api_key: apiKey.trim() });
      const res = await SyncAPI.saveConfig({ base_url: baseUrl.trim(), api_key: apiKey.trim() });
      // Validation is a SOFT SIGNAL — save always succeeds; we just surface the dot.
      if (res?.validation) setValidation(res.validation);
      toast.success("Sync configuration saved");
    } catch {
      toast.error("Saved locally, but failed to save to server");
    } finally {
      setSavingCfg(false);
    }
  };

  const reValidate = async () => {    if (!baseUrl.trim()) {
      toast.error("Enter a Base URL first");
      return;
    }
    setValidating(true);
    try {
      const v = await SyncAPI.validateUrl(baseUrl.trim(), true);
      setValidation(v);
      if (v.reachable) {
        toast.success(`REACHABLE · ${v.latency_ms}ms · ${v.probe}`);
      } else {
        toast.message(`UNREACHABLE · soft warning only — save still works`, {
          description: v.error ? String(v.error).slice(0, 80) : `http ${v.status || "n/a"}`,
        });
      }
      // Refresh sparkline after a fresh probe writes a new bar.
      try {
        const s = await SyncAPI.sparkline(baseUrl.trim(), 24);
        setSparkline(s);
      } catch (err) {
        console.error("sync: sparkline refresh failed", err);
      }
    } catch {
      toast.error("VALIDATION CHECK FAILED");
    } finally {
      setValidating(false);
    }
  };

  // iter-25: One-click end-to-end handshake — dry-run a tiny synthetic photo
  // upload to the parent ingest endpoint to confirm auth, path, and field
  // contract before the operator pushes real photos.
  const runHandshake = async () => {
    if (!baseUrl.trim() || !apiKey.trim()) {
      toast.error("Save Base URL + API key first");
      return;
    }
    setHandshaking(true);
    setHandshakeResult(null);
    try {
      const r = await SyncAPI.handshake({ base_url: baseUrl.trim(), api_key: apiKey.trim() });
      setHandshakeResult(r);
      if (r.ok) {
        toast.success(`HANDSHAKE PASS · ${r.latency_ms}ms · http ${r.status}`);
      } else {
        const map = {
          auth_failed: "AUTH REJECTED — check API key",
          bad_contract: "CONTRACT MISMATCH — check field names",
          unreachable: "UNREACHABLE — check base URL / network",
          rejected: `REJECTED · http ${r.status}`,
        };
        toast.error(map[r.decision] || `HANDSHAKE FAIL · http ${r.status || "n/a"}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "HANDSHAKE FAILED";
      toast.error(String(detail).toUpperCase().slice(0, 140));
    } finally {
      setHandshaking(false);
    }
  };

  const downloadZip = async () => {
    if (!active) return toast.error("No active session");
    setZipping(true);
    try {
      const photos = await Photos.list(active.id);
      if (!photos.length) {
        toast.error("Session has no photos");
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(active.name.replace(/[^a-zA-Z0-9-_]+/g, "-")) || zip;
      // Fetch each full photo + add to zip
      for (const meta of photos) {
        const full = await Photos.get(active.id, meta.id);
        const blob = dataUrlToBlob(full.image_data);
        folder.file(full.filename, blob);
      }
      // Manifest
      const manifest = {
        session: active,
        exported_at: new Date().toISOString(),
        photos: photos.map((p) => ({
          filename: p.filename,
          sequence: p.sequence,
          tags: p.tags,
        })),
      };
      folder.file("manifest.json", JSON.stringify(manifest, null, 2));

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.name.replace(/[^a-zA-Z0-9-_]+/g, "-")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${photos.length} photos`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to build ZIP");
    } finally {
      setZipping(false);
    }
  };

  const syncToDashboard = async () => {
    if (!active) return toast.error("No active session");
    if (!baseUrl.trim() || !apiKey.trim()) {
      toast.error("Configure Base URL and Key first");
      return;
    }
    setSyncing(true);
    setLastSyncResult(null);
    try {
      const res = await SyncAPI.syncSession(active.id, {
        base_url: baseUrl.trim(),
        api_key: apiKey.trim(),
      });
      setLastSyncResult(res);
      if (res.forwarded) {
        toast.success("Synced to AIImageSuite.pro");
      } else {
        toast.message("Sync recorded (endpoint did not confirm — placeholder mode)");
      }
    } catch (e) {
      console.error(e);
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const uploadPhotos = async (mode = "unsynced") => {
    if (!active) return toast.error("No active session");
    if (!baseUrl.trim() || !apiKey.trim()) {
      toast.error("Configure Base URL and Key first");
      return;
    }
    const list = mode === "all" ? photos : photos.filter((p) => !p.synced_at);
    if (list.length === 0) {
      toast.message(mode === "all" ? "No photos in session" : "Everything is already synced");
      return;
    }
    setUploading(true);
    setProgress({ sent: 0, total: list.length, ok: 0, failed: 0, current: null });
    let ok = 0;
    let failed = 0;
    try {
      for (let i = 0; i < list.length; i++) {
        const p = list[i];
        setProgress((prev) => ({ ...prev, current: p.filename, sent: i }));
        // Stable client UUID per photo so retries dedup on the server.
        const idemKey = `forward:${p.id}`;
        try {
          const res = await SyncAPI.forwardPhoto(active.id, p.id, {
            base_url: baseUrl.trim(),
            api_key: apiKey.trim(),
          }, idemKey);
          if (res.ok) ok += 1;
          else failed += 1;
        } catch {
          failed += 1;
        }
        setProgress((prev) => ({ ...prev, ok, failed, sent: i + 1 }));
      }
      await refreshPhotos();
      if (failed === 0) toast.success(`Forwarded ${ok} photos to AIImageSuite.pro`);
      else if (ok === 0) toast.error(`All ${failed} forwards failed — check URL / API key`);
      else toast.message(`Forwarded ${ok}, failed ${failed} — check endpoint contract`);
    } finally {
      setUploading(false);
    }
  };

  const targetUrlPreview = () => {
    if (!baseUrl.trim() || !contract?.ingest_path_template) return null;
    const sid = active?.id || "{session_id}";
    const path = contract.ingest_path_template.replace("{session_id}", sid);
    return `${baseUrl.trim().replace(/\/$/, "")}${path}`;
  };

  return (
    <div className="px-4 py-6 max-w-3xl mx-auto page-fade" data-testid="sync-page">
      <p className="label-mono text-neutral-500">04 / SYNC</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
        SYNC &amp; EXPORT
      </h1>
      <p className="text-sm text-neutral-600 mt-2 mb-8 max-w-lg">
        Export the active session as a ZIP, or push it to your AIImageSuite.pro dashboard.
      </p>

      {/* ============ UPGRADE BANNER (iter-17) ============ */}
      {syncEnabled ? (
        <div
          className="hairline-2 p-4 mb-8 bg-white flex items-center gap-3 flex-wrap"
          data-testid="sync-paid-banner"
        >
          <CheckCircle size={20} weight="fill" className="text-emerald-600" />
          <div className="flex-1 min-w-0">
            <p className="label-mono text-emerald-700">SYNC UNLOCKED</p>
            <p className="font-mono text-xs text-neutral-600 truncate">
              {billingEmail} · cloud sync + webhook automation active
            </p>
          </div>
          <button
            type="button"
            onClick={openPortal}
            disabled={portalLoading}
            className="btn-flat-outline text-xs whitespace-nowrap disabled:opacity-50"
            data-testid="sync-manage-subscription-btn"
            aria-label="Manage subscription"
          >
            {portalLoading ? "OPENING…" : "MANAGE SUBSCRIPTION"}
          </button>
        </div>
      ) : (
        <div
          className="hairline-2 p-5 mb-8 bg-white"
          data-testid="sync-upgrade-banner"
          style={{ boxShadow: "4px 4px 0 0 #FF2A2A" }}
        >
          <div className="flex items-baseline gap-2 mb-2">
            <Sparkle size={16} weight="bold" />
            <p className="label-mono text-neutral-500">CLOUD SYNC · $12/MO</p>
          </div>
          <p className="font-display text-2xl sm:text-3xl font-black tracking-tighter leading-tight">
            SEND TO AIIMAGESUITE.PRO
          </p>
          <p className="font-mono text-sm text-neutral-600 mt-2 mb-4 max-w-lg">
            Cloud backup, multi-device, webhook automation. Your captures
            live everywhere — not just on this phone. Cancel anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              type="email"
              data-testid="sync-upgrade-email-input"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="you@studio.com"
              className="flex-1 border-2 border-black px-3 py-2 font-mono text-sm"
            />
            <button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="btn-flat text-sm px-5 py-2 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="sync-upgrade-btn"
            >
              {upgrading ? (
                <>
                  <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
                  REDIRECTING…
                </>
              ) : (
                <>
                  <Sparkle size={14} weight="bold" /> TRY CLOUD SYNC
                </>
              )}
            </button>
          </div>
          <p className="label-mono text-neutral-400 mt-3 text-[10px]">
            STRIPE SECURED · CANCEL ANYTIME · $12/MONTH
          </p>

          {/* ============ Parallel Stripe Buy Button (Option B) ============
              Independent of STRIPE_PRICE_ID_STARTER env var — price baked into
              the button. Stripe captures buyer email natively; our webhook
              extracts it from customer_details.email to flip the user record. */}
          <div className="mt-5 pt-4 border-t-2 border-dashed border-neutral-300" data-testid="sync-buybutton-block">
            <p className="label-mono text-neutral-500 mb-2">OR — QUICK CHECKOUT</p>
            <p className="font-mono text-[11px] text-neutral-600 mb-3">
              Pay directly on Stripe. Same plan, same price. You&apos;ll enter your email on the checkout page.
            </p>
            <stripe-buy-button
              buy-button-id="buy_btn_1Tcfzm2O0Bmb4ui6IE1tuez5"
              publishable-key="pk_live_51TD0JH2O0Bmb4ui65qxhK1wclPzlQgb6OTOEBHDJjYO04p3QKoNL52qW4Qfdqp0OIgrp9Kr2DtKicFp4sfiWELKE00KF9mtics"
            />
            <p className="label-mono text-neutral-400 mt-2 text-[10px]">
              AFTER PAYMENT — RETURN HERE AND ENTER THE SAME EMAIL ABOVE TO ACTIVATE SYNC
            </p>
          </div>
        </div>
      )}

      {!active ? (
        <div className="hairline-2 p-6 mb-8" data-testid="sync-no-active">
          <p className="font-display text-xl font-bold mb-2">NO ACTIVE SESSION</p>
          <p className="text-sm text-neutral-600 mb-4">
            Activate a session from the Sessions list to export or sync it.
          </p>
          <button onClick={() => nav("/sessions")} className="btn-flat" data-testid="sync-go-sessions">
            Go to Sessions
          </button>
        </div>
      ) : (
        <div className="hairline-2 p-5 mb-8 bg-neutral-50" data-testid="sync-active-card">
          <p className="label-mono text-neutral-500">ACTIVE SESSION</p>
          <p className="font-display text-2xl font-bold leading-tight">{active.name}</p>
          <p className="font-mono text-sm text-neutral-600 break-all">{active.base_tags}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="chip">{photos.length} TOTAL</span>
            <span className="chip chip-blue" data-testid="sync-synced-count">
              {syncedCount} SYNCED
            </span>
            {unsyncedCount > 0 && (
              <span className="chip chip-live" data-testid="sync-unsynced-count">
                {unsyncedCount} PENDING
              </span>
            )}
          </div>
        </div>
      )}

      {/* Export ZIP */}
      <section className="mb-10">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3 border-b-2 border-black pb-1">
          A. EXPORT
        </h2>
        <p className="text-sm text-neutral-600 mb-4">
          Download a ZIP of all renamed JPEGs (with EXIF metadata baked in) plus a manifest.json.
        </p>
        <button
          onClick={downloadZip}
          disabled={!active || zipping}
          className="btn-flat w-full py-4"
          data-testid="download-zip-btn"
        >
          <DownloadSimple weight="bold" size={20} />
          {zipping ? "Building ZIP…" : "Download ZIP"}
        </button>
      </section>

      {/* Sync config */}
      <section className="mb-10">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3 border-b-2 border-black pb-1">
          B. AIIMAGESUITE.PRO CONFIG
        </h2>
        <p className="text-sm text-neutral-600 mb-4">
          Configure the <strong>Base URL</strong>; the versioned ingest path is appended
          automatically. Lets you switch between production / staging / dev without app rewrites.
        </p>
        <div className="space-y-4">
          <label className="block">
            <span className="label-mono block mb-2">Base URL</span>
            <div className="flex items-stretch border-2 border-black">
              <span className="w-12 flex items-center justify-center bg-black text-white">
                <LinkIcon size={18} weight="bold" />
              </span>
              <input
                type="url"
                placeholder="https://aiimagesuite.pro/api"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                className="flex-1 h-12 px-3 font-mono text-sm focus:outline-none focus:ring-4 focus:ring-[#002FA7]"
                data-testid="sync-base-url-input"
              />
            </div>
          </label>
          <label className="block">
            <span className="label-mono block mb-2">API Key</span>
            <div className="flex items-stretch border-2 border-black">
              <span className="w-12 flex items-center justify-center bg-black text-white">
                <Key size={18} weight="bold" />
              </span>
              <input
                type="password"
                placeholder="sk_live_xxx…"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="flex-1 h-12 px-3 font-mono text-sm focus:outline-none focus:ring-4 focus:ring-[#002FA7]"
                data-testid="sync-api-key-input"
              />
            </div>
          </label>
          <button
            onClick={saveConfig}
            disabled={savingCfg}
            className="btn-flat-outline w-full py-3"
            data-testid="sync-save-config-btn"
          >
            <FloppyDisk weight="bold" size={18} />
            {savingCfg ? "Saving…" : "Save Configuration"}
          </button>

          {/* Reachability chip — SOFT SIGNAL only. Save is never blocked. */}
          <div
            className="flex items-center justify-between gap-2 flex-wrap p-3 hairline-2"
            data-testid="ingest-validation-chip"
            style={{
              background: validation
                ? (validation.reachable ? "rgba(0,170,90,0.06)" : "rgba(255,42,42,0.06)")
                : "rgba(0,0,0,0.03)",
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="inline-block w-3 h-3 border-2 border-black shrink-0"
                style={{
                  background: !validation
                    ? "#FFFFFF"
                    : validation.reachable ? "#00AA5A" : "#FF2A2A",
                }}
                data-testid="ingest-validation-dot"
                aria-label={validation
                  ? (validation.reachable ? "ingest reachable" : "ingest unreachable")
                  : "ingest not yet probed"}
              />
              <span className="font-mono text-[11px] truncate">
                {!validation ? (
                  <span className="text-neutral-500">REACHABILITY UNKNOWN — click VALIDATE</span>
                ) : validation.reachable ? (
                  <>
                    REACHABLE · <b>{validation.latency_ms}ms</b> · http {validation.status} ·{" "}
                    <span className="opacity-60">{validation.probe}</span>
                  </>
                ) : (
                  <>
                    <span className="text-[#FF2A2A]">UNREACHABLE</span>{" "}
                    <span className="opacity-70">
                      · soft warning, save still works
                      {validation.error && ` · ${String(validation.error).slice(0, 60)}`}
                    </span>
                  </>
                )}
              </span>
            </div>
            <button
              onClick={reValidate}
              disabled={validating || !baseUrl.trim()}
              className="btn-flat-outline shrink-0"
              data-testid="ingest-validate-btn"
              title="Force re-probe (bypasses 15m cache)"
            >
              <ArrowsClockwise weight="bold" size={14} />
              {validating ? "PROBING…" : "VALIDATE"}
            </button>
          </div>

          {/* iter-25: End-to-end handshake — sends a 1×1 JPEG with the full
              ingest contract to AIImageSuite.pro and reports pass/fail. */}
          <div className="hairline-2 p-3" data-testid="handshake-block">
            <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
              <div className="min-w-0">
                <p className="label-mono text-neutral-500">HANDSHAKE</p>
                <p className="font-mono text-[11px] text-neutral-600 leading-snug max-w-md">
                  Sends a 1×1 JPEG with the full ingest contract — confirms
                  auth, path, and metadata field names in one click. No real
                  photos uploaded.
                </p>
              </div>
              <button
                onClick={runHandshake}
                disabled={handshaking || !baseUrl.trim() || !apiKey.trim()}
                className="btn-flat shrink-0"
                data-testid="handshake-run-btn"
              >
                <CloudArrowUp weight="bold" size={14} />
                {handshaking ? "TESTING…" : "TEST HANDSHAKE"}
              </button>
            </div>
            {handshakeResult && (
              <div
                className="mt-2 p-3 border-2 border-black bg-white"
                data-testid="handshake-result"
                style={{
                  background: handshakeResult.ok
                    ? "rgba(0,170,90,0.06)"
                    : "rgba(255,42,42,0.06)",
                }}
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {handshakeResult.ok ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-700" />
                  ) : (
                    <WarningCircle size={16} weight="fill" className="text-[#FF2A2A]" />
                  )}
                  <span className="font-display font-bold text-sm">
                    {handshakeResult.ok ? "PASS" : "FAIL"}
                  </span>
                  <span className="chip">
                    {(handshakeResult.decision || "").toUpperCase()}
                  </span>
                  <span className="label-mono text-neutral-500">
                    http {handshakeResult.status ?? "n/a"} · {handshakeResult.latency_ms}ms
                  </span>
                </div>
                <p className="font-mono text-[10px] text-neutral-600 break-all mb-2">
                  <span className="text-neutral-400">TARGET</span>{" "}
                  {handshakeResult.target_url}
                </p>
                {handshakeResult.sent_fields?.length > 0 && (
                  <p className="font-mono text-[10px] text-neutral-600 mb-2">
                    <span className="text-neutral-400">SENT FIELDS</span>{" "}
                    {handshakeResult.sent_fields.join(" · ")}{" "}
                    <span className="text-neutral-400">
                      ({handshakeResult.sent_bytes}B image)
                    </span>
                  </p>
                )}
                {handshakeResult.response_body && (
                  <details className="font-mono text-[10px]">
                    <summary className="cursor-pointer text-neutral-500 hover:text-black">
                      RESPONSE BODY ({handshakeResult.response_body.length}B)
                    </summary>
                    <pre
                      className="mt-1 p-2 bg-neutral-50 border border-neutral-300 whitespace-pre-wrap break-all max-h-40 overflow-auto"
                      data-testid="handshake-response-body"
                    >
                      {handshakeResult.response_body}
                    </pre>
                  </details>
                )}
                {handshakeResult.error && (
                  <p className="font-mono text-[10px] text-[#FF2A2A] mt-1 break-all">
                    {handshakeResult.error}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* 24h reachability sparkline — observability only, async-logged */}
          {sparkline && sparkline.count > 0 && (
            <div
              className="hairline-2 p-3"
              data-testid="ingest-sparkline"
              style={{ background: "rgba(0,0,0,0.02)" }}
            >
              <div className="flex items-baseline justify-between mb-2 gap-2">
                <p className="label-mono text-neutral-500">
                  REACHABILITY · {sparkline.window_hours}h
                </p>
                <p
                  className="font-mono text-[11px]"
                  data-testid="ingest-sparkline-success-rate"
                >
                  <span className="text-neutral-500">success</span>{" "}
                  <span className="font-bold">
                    {Math.round((sparkline.success_rate ?? 0) * 100)}%
                  </span>{" "}
                  <span className="opacity-50">· {sparkline.count} probes</span>
                </p>
              </div>
              <div
                className="flex items-end gap-[2px] h-10 border-2 border-black px-1 py-1 bg-white"
                data-testid="ingest-sparkline-bars"
              >
                {(() => {
                  // Render up to 48 bars; older first → newer last.
                  const items = sparkline.items.slice(-48);
                  const max = Math.max(1, ...items.map((i) => i.latency_ms || 1));
                  return items.map((row) => {
                    const h = Math.max(3, Math.round((row.latency_ms / max) * 30));
                    const color =
                      row.status === "success"
                        ? "#00AA5A"
                        : row.status === "timeout"
                        ? "#FFB000"
                        : "#FF2A2A";
                    return (
                      <span
                        key={row.ts + "-" + row.latency_ms}
                        title={`${new Date(row.ts * 1000).toISOString()} · ${row.status} · ${row.latency_ms}ms`}
                        className="flex-1 min-w-[2px]"
                        style={{ height: h + "px", background: color }}
                        data-testid={`sparkline-bar-${row.status}`}
                      />
                    );
                  });
                })()}
              </div>
              <p className="font-mono text-[10px] opacity-50 mt-1">
                green=success · amber=timeout · red=failed · height=latency
              </p>
            </div>
          )}

          {/* Ingest contract preview */}
          {contract && (
            <div className="hairline-2 p-3 bg-neutral-50" data-testid="ingest-contract-preview">
              <p className="label-mono text-neutral-500 mb-2">INGEST CONTRACT</p>
              <div className="space-y-1.5 font-mono text-[11px] break-all">
                <p>
                  <span className="text-neutral-500">PATH</span>{" "}
                  <span className="font-bold">{contract.ingest_path_template}</span>
                </p>
                {targetUrlPreview() && (
                  <p>
                    <span className="text-neutral-500">RESOLVED</span>{" "}
                    <span className="font-bold">{targetUrlPreview()}</span>
                  </p>
                )}
                {contract.default_upload_mode && (
                  <p>
                    <span className="text-neutral-500">MODE</span>{" "}
                    {contract.default_upload_mode} (default of {(contract.upload_modes || []).join(" / ")})
                  </p>
                )}
                {contract.events && (
                  <p>
                    <span className="text-neutral-500">EVENTS</span>{" "}
                    {contract.events.join(" · ")}
                  </p>
                )}
                {contract.receipt_ttl_days && (
                  <p>
                    <span className="text-neutral-500">RECEIPT TTL</span>{" "}
                    {contract.receipt_ttl_days} days
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sync to dashboard */}
      <section className="mb-12">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-3 border-b-2 border-black pb-1">
          C. SYNC TO DASHBOARD
        </h2>
        <p className="text-sm text-neutral-600 mb-4">
          Uploads each photo (with EXIF baked in) to your configured ingest endpoint. The summary
          button below sends only a session manifest (lightweight ping).
        </p>

        {/* Upload Photos with progress */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => uploadPhotos("unsynced")}
              disabled={!active || uploading}
              className="btn-flat btn-sync py-4"
              data-testid="upload-unsynced-btn"
            >
              <CloudArrowUp weight="bold" size={20} />
              {uploading ? "Uploading…" : `Upload ${unsyncedCount} Pending`}
            </button>
            <button
              onClick={() => uploadPhotos("all")}
              disabled={!active || uploading || photos.length === 0}
              className="btn-flat-outline py-4"
              data-testid="upload-all-btn"
            >
              <ArrowsClockwise weight="bold" size={20} />
              Re-sync All
            </button>
          </div>

          {progress && (
            <div className="hairline-2 p-3" data-testid="upload-progress">
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="label-mono">
                  {progress.sent} / {progress.total}
                </span>
                <span className="font-mono text-[11px] truncate">
                  {progress.current || ""}
                </span>
              </div>
              <div className="h-3 bg-neutral-200 border border-black relative">
                <div
                  className="h-full bg-[#002FA7] transition-all"
                  style={{ width: `${(progress.sent / progress.total) * 100}%` }}
                />
              </div>
              <div className="mt-2 flex gap-2">
                {progress.ok > 0 && (
                  <span className="chip chip-blue">{progress.ok} OK</span>
                )}
                {progress.failed > 0 && (
                  <span className="chip chip-live">{progress.failed} FAILED</span>
                )}
              </div>
            </div>
          )}

          <button
            onClick={syncToDashboard}
            disabled={!active || syncing}
            className="btn-flat-outline w-full py-3"
            data-testid="sync-to-dashboard-btn"
          >
            <CloudArrowUp weight="bold" size={18} />
            {syncing ? "Pinging…" : "Send Session Summary (Ping)"}
          </button>
        </div>

        {lastSyncResult && (
          <div
            className={`mt-4 p-4 border-2 ${
              lastSyncResult.forwarded ? "border-black bg-white" : "border-black bg-neutral-100"
            }`}
            data-testid="sync-result"
          >
            <div className="flex items-center gap-2 mb-2">
              {lastSyncResult.forwarded ? (
                <CheckCircle weight="fill" size={20} />
              ) : (
                <WarningCircle weight="fill" size={20} />
              )}
              <span className="font-display font-bold">
                {lastSyncResult.forwarded ? "FORWARDED" : "PLACEHOLDER MODE"}
              </span>
            </div>
            <pre className="font-mono text-[11px] whitespace-pre-wrap break-all">
              {JSON.stringify(lastSyncResult, null, 2)}
            </pre>
          </div>
        )}
      </section>

      {/* Sync receipts */}
      {active && (
        <section className="mb-12" data-testid="receipts-section">
          <div className="flex items-baseline justify-between border-b-2 border-black mb-3 pb-1">
            <h2 className="font-display text-2xl font-bold tracking-tight">D. SYNC RECEIPTS</h2>
            <div className="flex items-center gap-2">
              <span className="label-mono">{receipts.length} TOTAL</span>
              {receipts.length > 0 && (
                <button
                  onClick={clearAllReceipts}
                  className="label-mono underline text-neutral-600 hover:text-black"
                  data-testid="clear-receipts-btn"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            Audit trail of every per-photo sync attempt for this session.
          </p>

          {receipts.length === 0 ? (
            <div className="hairline-2 p-6 text-center text-neutral-500" data-testid="receipts-empty">
              <p className="label-mono">NO RECEIPTS YET</p>
              <p className="text-xs mt-1">Run an upload to start the audit log.</p>
            </div>
          ) : (
            <ul className="divide-y-2 divide-black border-2 border-black bg-white">
              {receipts.map((r) => (
                <li
                  key={r.id}
                  className="p-3 flex items-start justify-between gap-3"
                  data-testid={`receipt-row-${r.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`chip ${r.ok ? "chip-blue" : "chip-live"}`}>
                        {r.ok ? "OK" : "FAIL"}
                      </span>
                      <span className="label-mono text-neutral-500">
                        {r.upload_mode?.toUpperCase()}
                        {r.status ? ` · ${r.status}` : ""}
                      </span>
                      <span className="label-mono text-neutral-500">
                        {fmtBytes(r.payload_bytes)}
                      </span>
                    </div>
                    <p className="font-mono text-xs truncate">{r.filename}</p>
                    <p className="font-mono text-[10px] text-neutral-500 truncate">
                      {r.target_url}
                    </p>
                    {r.error && (
                      <p className="font-mono text-[10px] text-[#FF2A2A] truncate mt-1">
                        {r.error}
                      </p>
                    )}
                    <p className="label-mono text-[10px] text-neutral-500 mt-1">
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => copyReceipt(r)}
                    className="px-2 py-1.5 border-2 border-black bg-white text-black hover:bg-black hover:text-white transition-colors active:scale-95 shrink-0 flex items-center gap-1.5"
                    data-testid={`copy-receipt-${r.id}`}
                    aria-label="Copy JSON"
                  >
                    <Copy size={14} weight="bold" />
                    <span className="label-mono">Copy</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function fmtBytes(b) {
  if (!b || b < 1024) return `${b || 0}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1024 / 1024).toFixed(2)}MB`;
}
